/*
 * Shared modal-dialog focus management.
 *
 * openDialog(dialogEl, openerEl, { onClose })
 *   - Marks background content (siblings of the dialog's branch, up to <body>)
 *     as `inert` so assistive tech and keyboard users cannot reach it.
 *   - Moves focus to the first focusable element inside the dialog.
 *   - Traps Tab / Shift+Tab within the dialog and closes on Escape.
 *
 * closeDialog(openerEl)
 *   - Removes `inert` from the previously hidden background.
 *   - Restores focus to the opener element.
 *   - Cleans up the keydown trap listener.
 *
 * The opener element's own branch is never inerted, so it stays interactive
 * for toggle-to-close and keeps existing outside-click handlers working.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'summary',
  'area[href]',
  'iframe',
  "[tabindex]:not([tabindex='-1'])",
].join(', ')

const state = {
  isOpen: false,
  opener: null,
  onClose: null,
  keydownHandler: null,
  inertedEls: [],
  dialogEl: null,
}

const getFocusable = (container) =>
  Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement,
  )

const setBackgroundInert = (dialogEl, openerEl) => {
  state.inertedEls = []
  let node = dialogEl
  while (node && node !== document.body && node.parentElement) {
    const parent = node.parentElement
    Array.from(parent.children).forEach((sibling) => {
      if (sibling === node) return
      if (openerEl && sibling.contains(openerEl)) return
      if (sibling.hasAttribute('inert')) return
      sibling.setAttribute('inert', '')
      state.inertedEls.push(sibling)
    })
    node = parent
  }
}

const clearBackgroundInert = () => {
  state.inertedEls.forEach((el) => el.removeAttribute('inert'))
  state.inertedEls = []
}

export const openDialog = (dialogEl, openerEl = null, { onClose = null } = {}) => {
  if (!dialogEl || state.isOpen) return

  state.isOpen = true
  state.dialogEl = dialogEl
  state.opener = openerEl || document.activeElement
  state.onClose = onClose

  setBackgroundInert(dialogEl, state.opener)

  state.keydownHandler = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      if (typeof state.onClose === 'function') {
        state.onClose()
      } else {
        closeDialog(state.opener)
      }
      return
    }

    if (event.key !== 'Tab') return

    const items = getFocusable(dialogEl)
    if (items.length === 0) {
      event.preventDefault()
      dialogEl.focus()
      return
    }

    const first = items[0]
    const last = items[items.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  document.addEventListener('keydown', state.keydownHandler)

  const focusable = getFocusable(dialogEl)
  const target = focusable[0] || dialogEl
  requestAnimationFrame(() => target.focus())
}

export const closeDialog = (openerEl = null) => {
  if (!state.isOpen) return

  const active = document.activeElement
  const focusInside = state.dialogEl && state.dialogEl.contains(active)

  clearBackgroundInert()

  if (state.keydownHandler) {
    document.removeEventListener('keydown', state.keydownHandler)
    state.keydownHandler = null
  }

  const restoreTo = openerEl || state.opener
  if (focusInside && restoreTo && typeof restoreTo.focus === 'function') {
    restoreTo.focus()
  }

  state.isOpen = false
  state.opener = null
  state.onClose = null
  state.dialogEl = null
}
